// Universal My Approval Inbox — Central Inbox across All Approval Modules
//
// Shows every item awaiting action from the logged-in approver across all
// configured approval modules (Monthly Inputs, New Joiners, Resignations, Expense Claims, etc.)
// Actions: Approve, Reject, Send Back, Edit Amount (if level allows editing)
// Route: /line-item-approval/inbox

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import MainLayout from '../components/MainLayout';
import {
  CheckCircle2, XCircle, RotateCcw, Pencil, History as HistoryIcon, RefreshCw, Layers, ShieldCheck, UserCheck, Inbox
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function fmtINR(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function LineItemApprovalInbox() {
  const [meta, setMeta] = useState({ modules: [], approver_types: [], input_types: [], input_type_labels: {} });
  const [companies, setCompanies] = useState([]);
  const [selCompany, setSelCompany] = useState('');
  const [selModule, setSelModule] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const [actionItem, setActionItem] = useState(null); // { item, action }
  const [remarks, setRemarks] = useState('');
  const [editedAmount, setEditedAmount] = useState('');
  const [historyItem, setHistoryItem] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${API_URL}/line-item-approval/meta`, { withCredentials: true });
        setMeta(m.data || {});
        const c = await axios.get(`${API_URL}/companies`, { withCredentials: true });
        setCompanies(c.data || []);
        if ((c.data || []).length) setSelCompany(c.data[0].id);
      } catch (e) {
        toast.error('Failed to load inbox metadata');
      }
    })();
  }, []);

  const load = useCallback(async () => {
    if (!selCompany) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({ company_id: selCompany });
      if (selModule) q.set('module', selModule);
      const { data } = await axios.get(`${API_URL}/line-item-approval/items/pending-for-me?${q.toString()}`, { withCredentials: true });
      setItems(data || []);
    } catch (e) {
      toast.error('Failed to load pending items');
    } finally {
      setLoading(false);
    }
  }, [selModule, selCompany]);

  useEffect(() => {
    load();
  }, [selCompany, selModule, load]);

  const openAction = (item, action) => {
    setActionItem({ item, action });
    setRemarks('');
    setEditedAmount(action === 'edit' ? String(item.current_amount ?? '') : '');
  };

  const closeAction = () => {
    setActionItem(null);
    setRemarks('');
    setEditedAmount('');
  };

  const submitAction = async () => {
    if (!actionItem) return;
    const { item, action } = actionItem;
    const body = { action, remarks };
    if (action === 'edit') {
      if (!editedAmount || Number.isNaN(Number(editedAmount))) return toast.error('Enter a valid edited amount');
      if (!remarks.trim()) return toast.error('Remarks are mandatory when editing amount');
      body.edited_amount = Number(editedAmount);
    }
    if ((action === 'reject' || action === 'send_back') && !remarks.trim()) {
      return toast.error('Remarks are mandatory for Reject / Send Back');
    }

    try {
      await axios.post(`${API_URL}/line-item-approval/items/${item.id}/action`, body, { withCredentials: true });
      toast.success(`Action '${action.replace('_', ' ')}' completed successfully`);
      closeAction();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const currentLevel = (it) => {
    const cur = it.current_level;
    const lvls = it.levels_snapshot || [];
    return (cur != null && cur < lvls.length) ? lvls[cur] : null;
  };

  const moduleLabel = (mCode) => {
    const found = (meta.modules || []).find((m) => m.code === mCode);
    return found ? found.label : meta.input_type_labels[mCode] || mCode;
  };

  return (
    <MainLayout>
      <div className="p-6 bg-slate-50 min-h-screen" data-testid="universal-inbox-page">
        {/* Header Banner */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
          <div>
            <div className="flex items-center gap-2">
              <span className="p-2 bg-indigo-50 text-indigo-600 rounded-lg"><Inbox className="w-5 h-5" /></span>
              <h1 className="text-xl font-bold text-slate-900">My Approval Inbox</h1>
            </div>
            <p className="text-sm text-slate-500 mt-1">
              Central inbox for all approval requests (Joiners, Resignations, Monthly Inputs, Claims, Payroll) awaiting your action.
            </p>
          </div>
          <button
            data-testid="universal-inbox-refresh"
            onClick={load}
            className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg px-4 py-2 text-sm border border-slate-200 transition-all"
          >
            <RefreshCw className="w-4 h-4" /> Refresh Inbox
          </button>
        </div>

        {/* Toolbar Filters */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <select
              data-testid="universal-inbox-company"
              value={selCompany}
              onChange={(e) => setSelCompany(e.target.value)}
              className="border border-slate-300 rounded-lg px-3.5 py-2 text-sm bg-white font-medium text-slate-800"
            >
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select
              data-testid="universal-inbox-module-select"
              value={selModule}
              onChange={(e) => setSelModule(e.target.value)}
              className="border border-slate-300 rounded-lg px-3.5 py-2 text-sm bg-slate-50 font-medium text-slate-800"
            >
              <option value="">All Approval Modules</option>
              {(meta.modules || []).map((m) => (
                <option key={m.code} value={m.code}>{m.label}</option>
              ))}
            </select>
          </div>
          <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full font-mono">
            {items.length} Pending Approval(s)
          </span>
        </div>

        {/* Table / Listing */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
              <tr>
                <th className="px-5 py-3">Employee</th>
                <th className="px-5 py-3">Approval Module</th>
                <th className="px-5 py-3 text-right">Amount</th>
                <th className="px-5 py-3">Period / Month</th>
                <th className="px-5 py-3">Current Level</th>
                <th className="px-5 py-3">Rule Applied</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-500 font-medium">
                    Loading pending items...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-12 text-center text-slate-400">
                    <UserCheck className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="font-semibold text-slate-600">Your inbox is clear!</p>
                    <p className="text-xs text-slate-400 mt-0.5">No pending items require your action at this time.</p>
                  </td>
                </tr>
              )}
              {items.map((it) => {
                const lvl = currentLevel(it);
                const modName = moduleLabel(it.module || it.input_type);
                return (
                  <tr key={it.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`inbox-row-${it.id}`}>
                    <td className="px-5 py-3 font-medium text-slate-900">
                      {it.employee_name || <span className="text-slate-400">—</span>}
                      <div className="text-xs text-slate-500 font-mono">{it.employee_code}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 bg-indigo-50 text-indigo-700 text-xs font-semibold px-2.5 py-1 rounded-md border border-indigo-100">
                        {modName}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-bold text-slate-900">
                      {fmtINR(it.current_amount)}
                      {it.edited_amount != null && it.edited_amount !== it.original_amount && (
                        <div className="text-[10px] text-slate-400 line-through">{fmtINR(it.original_amount)}</div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-600 font-medium">{it.payroll_month || '—'}</td>
                    <td className="px-5 py-3">
                      <div className="text-xs font-bold text-slate-800">{lvl?.name || 'Current Level'}</div>
                      <div className="text-[10px] text-slate-400 font-mono">
                        Step {(it.current_level ?? 0) + 1} of {(it.levels_snapshot || []).length}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500">{it.matched_workflow_name || '—'}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap space-x-1">
                      <button
                        onClick={() => openAction(it, 'approve')}
                        className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-md px-2.5 py-1 text-xs transition-colors shadow-sm"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                      </button>
                      <button
                        onClick={() => openAction(it, 'reject')}
                        className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-md px-2.5 py-1 text-xs transition-colors shadow-sm"
                      >
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                      <button
                        onClick={() => openAction(it, 'send_back')}
                        className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-md px-2.5 py-1 text-xs transition-colors shadow-sm"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Send Back
                      </button>
                      {lvl?.edit_permitted && (
                        <button
                          onClick={() => openAction(it, 'edit')}
                          className="inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md px-2.5 py-1 text-xs transition-colors shadow-sm"
                        >
                          <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                      )}
                      <button
                        onClick={() => setHistoryItem(it)}
                        className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-md px-2 py-1 text-xs transition-colors border border-slate-200"
                      >
                        <HistoryIcon className="w-3.5 h-3.5" /> Audit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Action Modal */}
        {actionItem && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="inbox-action-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <h3 className="text-base font-bold text-slate-900 capitalize">
                  {actionItem.action.replace('_', ' ')} Request — {actionItem.item.employee_name}
                </h3>
                <button onClick={closeAction} className="text-slate-400 hover:text-slate-600"><XCircle className="w-5 h-5" /></button>
              </div>
              <div className="p-6 space-y-4">
                <div className="text-xs bg-indigo-50 border border-indigo-100 p-3 rounded-lg text-indigo-900">
                  <strong>Module:</strong> {moduleLabel(actionItem.item.module || actionItem.item.input_type)} · <strong>Amount:</strong> {fmtINR(actionItem.item.current_amount)}
                </div>

                {actionItem.action === 'edit' && (
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">New Approved Amount (₹) *</label>
                    <input
                      type="number"
                      value={editedAmount}
                      onChange={(e) => setEditedAmount(e.target.value)}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Remarks {(actionItem.action !== 'approve') && <span className="text-rose-500">*</span>}
                  </label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    rows={3}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500"
                    placeholder={actionItem.action === 'approve' ? 'Optional comments...' : 'Mandatory remarks for action'}
                  />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
                <button onClick={closeAction} className="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200 rounded-lg">
                  Cancel
                </button>
                <button
                  onClick={submitAction}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 text-xs font-bold rounded-lg capitalize shadow-sm"
                >
                  Submit {actionItem.action.replace('_', ' ')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Audit History Modal */}
        {historyItem && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="inbox-history-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-200">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Complete Approval Audit Trail</h3>
                  <p className="text-xs text-slate-500">{historyItem.employee_name} ({historyItem.employee_code}) · {moduleLabel(historyItem.module)}</p>
                </div>
                <button onClick={() => setHistoryItem(null)} className="text-slate-400 hover:text-slate-600"><XCircle className="w-5 h-5" /></button>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 font-semibold text-slate-500 uppercase border-b border-slate-200">
                    <tr>
                      <th className="py-2.5 px-3">Date & Time</th>
                      <th className="py-2.5 px-3">Actor / Approver</th>
                      <th className="py-2.5 px-3">Action</th>
                      <th className="py-2.5 px-3">Status Transition</th>
                      <th className="py-2.5 px-3">Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(historyItem.history || []).map((h, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="py-2.5 px-3 font-mono text-slate-500">{h.at}</td>
                        <td className="py-2.5 px-3 font-semibold text-slate-800">{h.actor_name || h.actor_id}</td>
                        <td className="py-2.5 px-3 font-bold text-indigo-700 capitalize">{h.action}</td>
                        <td className="py-2.5 px-3 font-mono text-slate-600">{h.from_status || '—'} → {h.to_status || '—'}</td>
                        <td className="py-2.5 px-3 text-slate-600">{h.remarks || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
